import React, { useState, FormEvent, KeyboardEvent } from 'react'
import useVoiceInput from './useVoiceInput'

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 11a7 7 0 0 1-14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

interface ChatInputProps {
  onSendMessage: (message: string) => void
  disabled?: boolean
  isLoading?: boolean
  onStop?: () => void
}

function ChatInput({ onSendMessage, disabled, isLoading, onStop }: ChatInputProps) {
  const [inputValue, setInputValue] = useState('')

  const submitText = (text: string) => {
    if (text.trim() && !disabled) {
      onSendMessage(text)
      setInputValue('')
    }
  }

  const {
    isSupported: voiceSupported,
    isListening,
    error: voiceError,
    start: startVoice,
    stop: stopVoice,
  } = useVoiceInput({
    onTranscript: setInputValue,
    onAutoSend: (prompt) => {
      stopVoice()
      submitText(prompt)
    },
  })

  const send = () => {
    if (isListening) stopVoice()
    submitText(inputValue)
  }

  const handleMicClick = () => {
    if (isListening) {
      stopVoice()
    } else {
      startVoice(inputValue)
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    send()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  return (
    <div className="chatbot-input-container">
      <form className="chat-input-form" onSubmit={handleSubmit}>
        <input
          type="text"
          className="chat-input"
          placeholder={isListening ? 'Listening…' : 'Type a message...'}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
        />
        {voiceSupported && !isLoading && (
          <button
            type="button"
            className={`chat-mic-button${isListening ? ' recording' : ''}`}
            aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
            aria-pressed={isListening}
            title={isListening ? 'Stop voice input' : 'Speak your message'}
            disabled={disabled}
            onClick={handleMicClick}
          >
            <MicIcon />
          </button>
        )}
        {isLoading ? (
          <button
            type="button"
            className="chat-stop-button"
            aria-label="Stop generating response"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onStop?.()
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
            </svg>
          </button>
        ) : (
          <button
            type="submit"
            className="chat-send-button"
            disabled={disabled || !inputValue.trim()}
            aria-label="Send message"
          >
            <SendIcon />
          </button>
        )}
      </form>
      {isListening && !voiceError && (
        <div className="chat-voice-hint">Say “Go” or pause to send.</div>
      )}
      {voiceError && <div className="chat-voice-error" role="alert">{voiceError}</div>}
    </div>
  )
}

export default ChatInput
